import { Box, CircularProgress, Alert } from '@mui/material'
import { useParams } from 'react-router'
import { useOne } from '@refinedev/core'
import { useState, useEffect } from 'react'
import type { TPublisherRequest } from '@modules/types'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'
import { useEnumContext } from '@modules/enums/context/enum'
import { ListView } from '@ds/components'
import { useAnalytics } from '@publisher/modules/analytics/hooks'
import { TRequestEventName } from '@publisher/modules/analytics/channels'
import { formatRequestData } from '@utils/requestFormatter'
import { RequestShow } from '../RequestShow'

const TITLE_POSTFIX = 'Publisher Panel'

type TSharing = {
  id: number
  requestId: number
  createdAt: string
}

export const ShareShow = () => {
  const { id } = useParams<{ id: string }>()
  const { enums, loading: enumsLoading } = useEnumContext()
  const { analytics, channels } = useAnalytics()
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set())

  const {
    data: sharingData,
    isLoading: sharingLoading,
    isError: sharingError,
  } = useOne<TSharing>({
    resource: 'sharing',
    id,
    dataProviderName: 'share',
    queryOptions: {
      enabled: !!id,
    },
  })

  const sharing = sharingData?.data
  const sharingRequestId = sharing?.requestId

  const {
    data: requestData,
    isLoading: requestLoading,
    isError: requestError,
  } = useOne<TPublisherRequest>({
    resource: 'publisher-request',
    id: sharingRequestId,
    dataProviderName: 'share',
    meta: PUBLISHER_EXPAND_PRESETS.CLIENT_REQUEST,
    queryOptions: {
      enabled: !!sharingRequestId,
    },
  })

  const request = requestData?.data
  const isLoading = sharingLoading || requestLoading
  const isError = sharingError || requestError

  useEffect(() => {
    if (request && analytics && channels.request) {
      channels.request.send({
        event: TRequestEventName.Open,
        requestId: request.id,
      })
    }
  }, [request, analytics, channels.request])

  useEffect(() => {
    const title = request?.clientRequest?.id
    document.title = title ? `${title} | ${TITLE_POSTFIX}` : TITLE_POSTFIX
    return () => {
      document.title = TITLE_POSTFIX
    }
  }, [request])

  const handleToggleCard = (requestId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(requestId)) {
        newSet.delete(requestId)
      } else {
        newSet.add(requestId)
      }
      return newSet
    })
  }

  if (isLoading) {
    return (
      <ListView overflow>
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </ListView>
    )
  }

  if (isError || !sharing || !request || !sharingRequestId) {
    return (
      <ListView overflow>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            {isError ? 'Error loading data' : 'Sharing or request not found'}
          </Alert>
        </Box>
      </ListView>
    )
  }

  const formattedRequestData = formatRequestData(request, enums, enumsLoading)

  return (
    <ListView overflow>
      <Box>
        <RequestShow
          request={request}
          formattedData={formattedRequestData}
          isExpanded={expandedCards.has(sharingRequestId)}
          onToggleExpand={() => handleToggleCard(sharingRequestId)}
          sharingRequestId={sharingRequestId}
        />
      </Box>
    </ListView>
  )
}
